import { CustomTaskOrder, GroupByMode, ID } from '@griply/types';
import { endOfDay } from 'date-fns';

import { stripUndefined } from '../utils/strip-undefined';

// The old iOS app writes only the legacy `todoSorting` / `todayGroupSorting`, so the
// new `customTaskOrder` store can go stale. A legacy entry that diverges from the new
// store overwrites it; an absent/null/empty legacy entry keeps the new store.

const coerceIdArray = (value: unknown): ID[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter((entry): entry is ID => typeof entry === 'string');
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const sameOrder = (a: ID[], b: ID[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

const isTodayBucketKey = (key: string, today: Date): boolean => {
  const date = new Date(key);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date.getTime() <= endOfDay(today).getTime();
};

const reconcileRecord = (
  newRecord: Record<ID, ID[]> | undefined,
  legacyRecord: unknown,
  keyFilter?: (key: string) => boolean,
): Record<ID, ID[]> | undefined => {
  if (!isRecord(legacyRecord)) {
    return newRecord;
  }

  const result: Record<ID, ID[]> = { ...(newRecord ?? {}) };
  let changed = false;

  for (const [key, rawValue] of Object.entries(legacyRecord)) {
    if (keyFilter && !keyFilter(key)) {
      continue;
    }
    const legacyIds = coerceIdArray(rawValue);
    if (legacyIds === undefined || !legacyIds.length) {
      continue;
    }
    const newIds = result[key];
    if (!newIds || !sameOrder(legacyIds, newIds)) {
      result[key] = legacyIds;
      changed = true;
    }
  }

  if (!changed) {
    return newRecord;
  }
  return result;
};

// None's legacy lives in the date-keyed today buckets of the shared `todoSorting`.
const reconcileNone = (
  newFlat: ID[] | undefined,
  todoSorting: unknown,
  today: Date,
): ID[] | undefined => {
  if (!isRecord(todoSorting)) {
    return newFlat;
  }

  const legacyFlat = Object.entries(todoSorting)
    .filter(([key]) => isTodayBucketKey(key, today))
    .map(([key, value]): [string, ID[] | undefined] => [
      key,
      coerceIdArray(value),
    ])
    .filter((entry): entry is [string, ID[]] => !!entry[1]?.length)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .flatMap(([, ids]) => ids);

  if (!legacyFlat.length) {
    return newFlat;
  }
  if (newFlat && sameOrder(legacyFlat, newFlat)) {
    return newFlat;
  }
  return legacyFlat;
};

const TODAY_GROUPED_MODES: [Exclude<GroupByMode, GroupByMode.None>, string][] =
  [
    [GroupByMode.Goal, 'goal'],
    [GroupByMode.LifeArea, 'lifeArea'],
    [GroupByMode.Priority, 'priority'],
    [GroupByMode.Type, 'type'],
    [GroupByMode.DayPart, 'dayPart'],
  ];

export const reconcileCustomTaskOrder = (
  coerced: CustomTaskOrder | null | undefined,
  settings: Record<string, unknown>,
  today: Date,
): CustomTaskOrder | null | undefined => {
  if (coerced === null) {
    return null;
  }

  const base = coerced ?? {};
  const todoSorting = settings.todoSorting;
  const todayGroupSorting = isRecord(settings.todayGroupSorting)
    ? settings.todayGroupSorting
    : undefined;

  const reconciledToday = stripUndefined({
    ...(base.today ?? {}),
    [GroupByMode.None]: reconcileNone(
      base.today?.[GroupByMode.None],
      todoSorting,
      today,
    ),
    [GroupByMode.Deadline]: reconcileRecord(
      base.today?.[GroupByMode.Deadline],
      todoSorting,
      (key) => isTodayBucketKey(key, today),
    ),
    ...TODAY_GROUPED_MODES.reduce<Record<string, Record<ID, ID[]> | undefined>>(
      (acc, [mode, legacyKey]) => {
        const legacySorting = isRecord(todayGroupSorting?.[legacyKey])
          ? (todayGroupSorting?.[legacyKey] as Record<string, unknown>).sorting
          : undefined;
        acc[mode] = reconcileRecord(base.today?.[mode], legacySorting);
        return acc;
      },
      {},
    ),
  });

  const reconciledUpcoming = stripUndefined({
    ...(base.upcoming ?? {}),
    [GroupByMode.UpcomingDeadline]: reconcileRecord(
      base.upcoming?.[GroupByMode.UpcomingDeadline],
      todoSorting,
      (key) => !isTodayBucketKey(key, today),
    ),
  });

  const result: CustomTaskOrder = stripUndefined({
    ...base,
    today: Object.keys(reconciledToday).length ? reconciledToday : undefined,
    upcoming: Object.keys(reconciledUpcoming).length
      ? reconciledUpcoming
      : undefined,
  });

  if (coerced === undefined && !Object.keys(result).length) {
    return undefined;
  }
  return result;
};
